import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Share2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

/**
 * Order Receipt · Section 5.
 *
 * Pulls GET /api/orders/{id}/receipt which is server-authoritative and
 * includes refunds + loyalty + tip + full customization. Printable via
 * window.print and shareable via Web Share API when available.
 */
function fmt(c) { return `$${(Number(c || 0) / 100).toFixed(2)}`; }

export default function Receipt() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}/receipt`);
        setR(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Couldn't load receipt");
      }
    })();
  }, [orderId]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Receipt ${r.receipt_id}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch { /* user cancelled */ }
  };

  if (err) return <div className="min-h-screen bg-black text-white grid place-items-center"><p>{err}</p></div>;
  if (!r) return <div className="min-h-screen bg-black text-white grid place-items-center"><p>Loading…</p></div>;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="receipt-page">
      <div className="sticky z-20 backdrop-blur bg-black/80 border-b border-white/10 px-4 py-3 flex items-center gap-2 print:hidden" style={{ top: "calc(env(safe-area-inset-top, 0px) + 56px)" }}>
        <button onClick={() => navigate(-1)} className="w-10 h-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10" data-testid="receipt-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-lg font-black flex-1">Receipt</h1>
        <button onClick={() => window.print()} className="w-10 h-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10" data-testid="receipt-print">
          <Printer className="w-4 h-4" />
        </button>
        <button onClick={share} className="w-10 h-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10" data-testid="receipt-share">
          <Share2 className="w-4 h-4" />
        </button>
      </div>

      <div className="max-w-md mx-auto p-6 print:bg-white print:text-black print:max-w-full">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-black">{r.shop_name}</h2>
          {r.location_label && <p className="text-xs text-zinc-500 mt-0.5">{r.location_label}</p>}
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3">{r.receipt_id}</p>
        </div>

        <div className="grid grid-cols-2 gap-y-1 text-xs mb-6">
          <span className="text-zinc-400">Placed</span>
          <span className="text-right">{new Date(r.placed_at).toLocaleString()}</span>
          {r.completed_at && (
            <>
              <span className="text-zinc-400">Completed</span>
              <span className="text-right">{new Date(r.completed_at).toLocaleString()}</span>
            </>
          )}
          <span className="text-zinc-400">Fulfillment</span>
          <span className="text-right capitalize">{r.fulfillment_type}</span>
          <span className="text-zinc-400">Payment</span>
          <span className="text-right">{r.payment_method} · {r.payment_status.replace("_", " ")}</span>
          <span className="text-zinc-400">Customer</span>
          <span className="text-right">{r.buyer_name}</span>
        </div>

        <div className="border-t border-dashed border-white/10 print:border-black/40 pt-4 mb-4 space-y-3">
          {r.items.map((it) => (
            <div key={it.line_id || it.product_id} className="text-sm" data-testid={`receipt-item-${it.line_id || it.product_id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">× {it.quantity} {it.name}</span>
                <span className="font-bold">{fmt(it.line_total_cents)}</span>
              </div>
              {it.variation && <div className="text-xs text-zinc-500 pl-2">Size: {it.variation}</div>}
              {(it.modifiers || []).length > 0 && <div className="text-xs text-zinc-500 pl-2">+ {it.modifiers.join(", ")}</div>}
              {(it.ingredients || []).length > 0 && <div className="text-xs text-amber-300 pl-2 print:text-black/60">{it.ingredients.join(" · ")}</div>}
              {it.special_instructions && <div className="text-xs italic text-pink-300 pl-2 print:text-black/80">"{it.special_instructions}"</div>}
            </div>
          ))}
        </div>

        <div className="border-t border-dashed border-white/10 print:border-black/40 pt-4 space-y-1 text-sm">
          <Row k="Subtotal" v={fmt(r.subtotal_cents || (r.total_cents - r.tip_cents - r.tax_cents - (r.service_fee_cents || 0) + r.discount_cents + r.loyalty_discount_cents))} />
          {r.service_fee_cents > 0 && <Row k="Service fee" v={fmt(r.service_fee_cents)} />}
          {r.discount_cents > 0 && <Row k={`Promo${r.promotion_code ? ` (${r.promotion_code})` : ""}`} v={`-${fmt(r.discount_cents)}`} tone="text-emerald-300" />}
          {r.loyalty_discount_cents > 0 && <Row k={`Loyalty (${r.loyalty_points_redeemed} pts)`} v={`-${fmt(r.loyalty_discount_cents)}`} tone="text-emerald-300" />}
          {r.tax_cents > 0 && <Row k="Tax" v={fmt(r.tax_cents)} />}
          {r.tip_cents > 0 && <Row k="Tip" v={fmt(r.tip_cents)} />}
          <div className="border-t border-white/10 print:border-black/40 pt-2 flex justify-between text-base font-black">
            <span>Total</span><span>{fmt(r.total_cents)}</span>
          </div>
          {r.refunded_cents > 0 && (
            <>
              <Row k="Refunded" v={`-${fmt(r.refunded_cents)}`} tone="text-amber-300" />
              <div className="flex justify-between text-base font-black">
                <span>Net paid</span><span>{fmt(r.net_paid_cents)}</span>
              </div>
            </>
          )}
          {r.loyalty_points_earned > 0 && (
            <p className="text-xs text-pink-300 text-center pt-2">You earned {r.loyalty_points_earned} loyalty points ✨</p>
          )}
        </div>

        <p className="text-center text-[10px] text-zinc-500 mt-6">Generated {new Date(r.generated_at).toLocaleString()}</p>
      </div>
    </div>
  );
}

function Row({ k, v, tone }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-400 print:text-black/70">{k}</span>
      <span className={tone || ""}>{v}</span>
    </div>
  );
}
